import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UserContainer } from '@telmar-global/tup-auth';
import {
  TupDocument,
  TupDocumentService,
  TupUserContainerService,
} from '@telmar-global/tup-document-storage';

import { Subject, combineLatest, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  takeUntil,
  tap,
  delay,
  filter,
  first,
} from 'rxjs/operators';
import { isNotNullOrUndefined } from 'src/app/utils/pipeable-operators';

import { DialogService } from 'src/app/services/dialog.service';
import {
  CreateNewDocumentService,
  DataItemsService,
  DocumentService,
  ReportPreferencesService,
  RequestLoadingService,
  TargetLoading,
  TargetService,
  TitleLevelsService,
  TitleModeService,
  TopBarService,
  TrendingCalculationService,
} from '../../services';
import {
  CellStyleStatus,
  ChartTargetMode,
  DataItem,
  DisplayType,
  DocumentDataState,
  ReportMode,
  ReportPreference,
  Survey,
  SurveyTimeDocument,
  SWAP_ROWS_COLUMNS_WARNING,
  Target,
  TargetType,
  TITLE_MODES,
  ViewType,
} from '../../models';
import { TitleLevelsDialogResult } from '../../dialogs';
import { HighlightCellsService } from 'src/app/services/highlight-cells.service';

import {
  DocumentAudienceGroupItem,
  SaveOwnCodesTargetGroup,
  SaveOwnCodesType,
  TupAudienceGroupsService,
} from '@telmar-global/tup-audience-groups';
import { TupUserMessageService } from '@telmar-global/tup-user-message';
import { TargetTitlePipe } from '../../pipes';
import { SwapRowsColumnsAction } from '../../actions/SwapRowsColumnsAction';

@Component({
  selector: 'cross-tab',
  templateUrl: './cross-tab.component.html',
  styleUrls: ['./cross-tab.component.scss'],
})
export class CrossTabComponent implements OnInit, OnDestroy {
  private unsubscribe: Subject<void> = new Subject<void>();
  private hasCurrentDocument: boolean;
  private currentSurveys: Survey[] = [];
  public targetType: typeof TargetType = TargetType;
  public viewType: typeof ViewType = ViewType;
  public isReadonly = true;
  public saveOwnCodesType: typeof SaveOwnCodesType = SaveOwnCodesType;
  public reportMode: typeof ReportMode = ReportMode;
  public targetMode: ChartTargetMode = ChartTargetMode.single;
  public docOwnerName: string;
  public docName: string;
  public currentDocument: TupDocument<SurveyTimeDocument>;
  public surveys: Survey[];
  public visibleSurveys: Survey[];
  public selectedSurvey: Survey;
  public hiddenSurveys: Set<Survey> = new Set<Survey>();

  public rightSidebarOpened = false;

  public stabilityFlagOn = true;
  public zScoreHighlight = false;
  public significanceTestingVar = null;
  public titleMode: DisplayType;

  public displayTypes = DisplayType;
  public titleModes = TITLE_MODES;

  public activeReportMode: ReportMode;
  public isLoading = false;
  public swapActionWarning = '';

  constructor(
    private router: Router,
    private topBarService: TopBarService,
    private activatedRoute: ActivatedRoute,
    private userContainerService: TupUserContainerService,
    private documentService: DocumentService,
    private requestLoadingService: RequestLoadingService,
    private tupDocumentService: TupDocumentService,
    private messageService: TupUserMessageService,
    private targetService: TargetService,
    private swapRowsColumnsAction: SwapRowsColumnsAction,
    private dialog: DialogService,
    private colorCellService: HighlightCellsService,
    private audienceGroupsService: TupAudienceGroupsService,
    private targetTitlePipe: TargetTitlePipe,
    private titleModeService: TitleModeService,
    private titleLevelsService: TitleLevelsService,
    private dataItemsService: DataItemsService,
    private createNewDocumentService: CreateNewDocumentService,
    private trendingCalculationService: TrendingCalculationService,
    private reportPreferencesService: ReportPreferencesService
  ) {
    this.hasCurrentDocument =
      this.router.getCurrentNavigation().extras?.state?.hasDocument;

    this.currentSurveys =
      this.router.getCurrentNavigation().extras?.state?.surveys;

    this.getCurrentDocument();
  }

  ngOnInit() {
    this.listenToDocumentDataChanges();
    this.listenToLoadingState();
    this.listenToTitleModeUpdates();
    this.listenToActiveSurveyChanges();
    this.listenToReportModeChanges();
    this.listenToReportPreferencesChanges();
    this.listenToRightSideBarOpenedStatus();
  }

  private getCurrentDocument(): void {
    combineLatest([
      this.activatedRoute.params,
      this.userContainerService.container.pipe(isNotNullOrUndefined()),
    ])
      .pipe(
        takeUntil(this.unsubscribe),
        tap(() => this.documentService.restoreInitialDocumentState()),
        switchMap(([params, container]: [Params, UserContainer]) => {
          const documentId = params.documentId;

          if (this.hasCurrentDocument && this.currentSurveys.length) {
            return of([this.currentSurveys, true, documentId]);
          }
          return this.tupDocumentService.get(container.name, documentId).pipe(
            map((document: TupDocument<SurveyTimeDocument>) => {
              this.documentService.setCurrentDocument(document);
              const surveys = document?.content?.surveys ?? [];
              return [surveys, true, documentId, undefined];
            }),
            catchError(() => of([[], false, documentId]))
          );
        })
      )
      .subscribe(
        ([surveys, includeRowCol, documentId]: [
          Survey[],
          boolean,
          string | undefined
        ]) => {
          const hasSurveys = surveys && surveys.length;

          this.requestLoadingService.setLoading({
            target: 'global',
            isLoading: false,
          });
          if (hasSurveys) {
            this.createNewDocumentService.setListRecentSurveys(surveys);
            this.documentService.setActiveDocumentId(documentId);
            this.documentService.initDocument(
              surveys,
              includeRowCol,
              false,
              true,
              false
            );
          } else {
            if (documentId) {
              this.router.navigate(['404']);
            } else {
              this.router.navigate(['dashboard']);
            }
          }
        }
      );
  }

  public openHighlightDialog(): void {
    this.rightSidebarOpened = false;
    this.significanceTestingVar = null;
    this.colorCellService
      .highlightColors()
      .afterClosed()
      .pipe(isNotNullOrUndefined())
      .subscribe((data) => {
        this.reportPreferencesService.updateHighlightValues({
          ...data,
        });
      });
  }

  private listenToTitleModeUpdates(): void {
    this.titleModeService.titleMode$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((titleMode) => (this.titleMode = titleMode));
  }

  public selectTitleMode(): void {
    this.rightSidebarOpened = false;
    this.titleModeService.updateTitleMode(this.titleMode);
    this.documentService.updateDocumentDataTitle(
      {
        activeTitleMode: this.titleMode,
      },
      false
    );
  }

  public clickTitleMode(displayType: DisplayType): void {
    if (displayType === DisplayType.levels) {
      this.openTitleLevelsDialog();
    }
  }

  public resetTitleMode() {
    this.titleMode = this.displayTypes.title;
    this.selectTitleMode();
  }

  public openManageReportItemsDialog(): void {
    this.rightSidebarOpened = false;
    this.documentService.documentState$
      .pipe(first())
      .subscribe((state: DocumentDataState) =>
        this.dialog
          .manageReportItems(state)
          .afterClosed()
          .subscribe((result) => {
            if (result) {
              this.documentService.deleteDocumentData(result, true);
            }
          })
      );
  }

  public turnOnSignificanceTesting(): void {
    this.rightSidebarOpened = false;
    const significanceTestingOn =
      this.significanceTestingVar === 'row'
        ? CellStyleStatus.significanceTestingRow
        : CellStyleStatus.significanceTestingColumn;
    this.reportPreferencesService.updateCellStatus(significanceTestingOn);
  }

  public showStabilityFlag(): void {
    this.rightSidebarOpened = false;
    this.stabilityFlagOn = !this.stabilityFlagOn;
    this.reportPreferencesService.updateStabilityFlagStatus(
      this.stabilityFlagOn
    );
  }

  public toggleZScoreHighlight(): void {
    this.rightSidebarOpened = false;
    this.zScoreHighlight = !this.zScoreHighlight;
    this.reportPreferencesService.updateCellStatus(
      this.zScoreHighlight
        ? CellStyleStatus.zScoreHighlight
        : CellStyleStatus.none
    );
  }

  public openDataItemsDialog(): void {
    this.rightSidebarOpened = false;
    this.dialog
      .dataItems(this.dataItemsService.getDataItems(this.activeReportMode))
      .afterClosed()
      .pipe(isNotNullOrUndefined())
      .subscribe((items: DataItem[]) => {
        this.dataItemsService.updateDataItems(this.activeReportMode, items);
        this.documentService.notifyCrosstabDataAboutToChange();
        this.documentService.updateCrossTabData();
      });
  }

  public openHeatmapDialog(): void {
    this.rightSidebarOpened = false;
    this.significanceTestingVar = null;
    this.colorCellService
      .heatmap()
      .afterClosed()
      .pipe(isNotNullOrUndefined())
      .subscribe((data) => {
        this.reportPreferencesService.updateHeatmapPercentageValue(
          data.selectedHeatmapOption,
          data.value
        );
      });
  }

  ngOnDestroy(): void {
    this.resetTrendingCalculation();
    this.unsubscribeLoadingSubscription();
    this.documentService.unsetActiveDocumentId();
    this.documentService.unsetReadonlyDoc();
    this.reportPreferencesService.resetStates();
  }

  public openTitleLevelsDialog(): void {
    this.rightSidebarOpened = false;
    this.titleLevelsService
      .openDialog()
      .subscribe((dialogResult: TitleLevelsDialogResult) => {
        const target: Partial<Target> = {
          titleLevels: dialogResult.titleLevels,
        };
        this.titleLevelsService.updateTitleLevels(dialogResult.titleLevels);

        if (this.titleMode !== DisplayType.levels) {
          target.activeTitleMode = DisplayType.levels;
          this.titleModeService.updateTitleMode(target.activeTitleMode);
        }
        this.documentService.updateDocumentDataTitle(target, false);
      });
  }

  public swapRowsAndColumns(): void {
    this.rightSidebarOpened = false;
    this.swapRowsColumnsAction.invoke();
  }

  public saveOwnCodes(type: SaveOwnCodesType): void {
    this.rightSidebarOpened = false;
    this.audienceGroupsService
      .saveOwnCodes({
        survey: this.documentService.activeSurvey,
        type,
        fileName: '',
        targetGroups: this.formatSaveOWnCodesTargetGroups(),
      })
      .subscribe(
        () => {
          this.messageService.showSnackBar(
            'Custom audiences/media saved successfully',
            'OK',
            10000
          );
        },
        (error) => {
          this.messageService.showSnackBar(error, 'OK', 10000);
        }
      );
  }

  private formatSaveOWnCodesTargetGroups(): SaveOwnCodesTargetGroup[] {
    const targetGroups = [];
    this.documentService.documentState$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(({ tables, columns, rows }: DocumentDataState) => {
        targetGroups.push({
          title: 'bases*',
          selectAll: false,
          items: tables.map((target: Target) => ({
            selected: false,
            targetItem: this.convertTargetToAudienceGroupItem(target),
          })),
        });
        targetGroups.push({
          title: 'columns',
          selectAll: false,
          items: columns.map((target: Target) => ({
            selected: false,
            targetItem: this.convertTargetToAudienceGroupItem(target),
          })),
        });
        targetGroups.push({
          title: 'rows',
          selectAll: false,
          items: rows.map((target: Target) => ({
            selected: false,
            targetItem: this.convertTargetToAudienceGroupItem(target),
          })),
        });
      });

    return targetGroups;
  }

  private convertTargetToAudienceGroupItem(
    target: Target
  ): DocumentAudienceGroupItem {
    return {
      title: this.targetTitlePipe.transform(target),
      coding: target.coding,
      options: {
        statement: null,
        target: this.targetService.shallowCopyTarget(target),
      },
    };
  }

  private listenToLoadingState(): void {
    this.requestLoadingService.loading$
      .pipe(
        takeUntil(this.unsubscribe),
        filter(
          (targetLoading: TargetLoading) => targetLoading.target === 'global'
        )
      )
      .subscribe((targetLoading: TargetLoading) => {
        this.isLoading = targetLoading.isLoading;
      });
  }

  private listenToDocumentDataChanges(): void {
    this.documentService.currentDoc
      .pipe(isNotNullOrUndefined())
      .subscribe((doc: TupDocument<SurveyTimeDocument>) => {
        this.docOwnerName = doc.metadata.by.attributes.name;
        this.docName = doc.metadata.name;
        this.currentDocument = doc;
        this.surveys = doc.content.surveys;
      });

    this.documentService.readonlyDoc$
      .pipe(isNotNullOrUndefined(), takeUntil(this.unsubscribe))
      .subscribe((readonly: boolean) => {
        this.isReadonly = readonly;
      });

    this.documentService.restoreDocumentState$
      .pipe(takeUntil(this.unsubscribe), delay(0))
      .subscribe(() => {
        this.updateSurveys();
      });
  }

  private updateSurveys(): void {
    this.surveys = this.documentService.surveys;
    this.selectedSurvey = this.documentService.activeSurvey;
    this.visibleSurveys = this.documentService.getVisibleSurveys();
  }

  private listenToActiveSurveyChanges(): void {
    this.documentService.selectedSurvey$
      .pipe(takeUntil(this.unsubscribe), isNotNullOrUndefined())
      .subscribe((survey: Survey) => {
        this.swapActionWarning =
          survey.provider === 'YouGov' ? SWAP_ROWS_COLUMNS_WARNING : '';
        this.selectedSurvey = survey;
        this.visibleSurveys = this.documentService.getVisibleSurveys();
      });
  }

  private listenToReportModeChanges(): void {
    this.reportPreferencesService.reportMode$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((mode: ReportMode) => {
        this.activeReportMode = mode;
      });
  }

  private listenToReportPreferencesChanges(): void {
    this.reportPreferencesService.preference$
      .pipe(takeUntil(this.unsubscribe), isNotNullOrUndefined())
      .subscribe((preference: ReportPreference) => {
        this.zScoreHighlight =
          preference.cellStyleStatus === CellStyleStatus.zScoreHighlight;
      });
  }

  private unsubscribeLoadingSubscription(): void {
    this.isLoading = false;
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private listenToRightSideBarOpenedStatus(): void {
    this.topBarService.rightSidebarOpenedState$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((opened: boolean) => {
        this.rightSidebarOpened = opened;
      });
  }

  private resetTrendingCalculation(): void {
    this.trendingCalculationService.reset();
  }
}
